import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { 
  User,
  BookOpen,
  Camera,
  Save,
  ArrowLeft,
  Mail,
  Calendar,
  Clock,
  Phone,
  Bell,
  FileText,
  MessageSquare,
  GraduationCap,
  ClipboardCheck,
  Target
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

const SMS_PREF_OPTIONS = [
  { key: "new_assignments", label: "New Assignments", description: "When a new assignment is posted", icon: FileText },
  { key: "journal_response", label: "Journals Reviewed", description: "When your journal is reviewed", icon: BookOpen },
  { key: "forum_posts", label: "Forum Topics", description: "When a new forum topic is added", icon: MessageSquare },
  { key: "messages_received", label: "Messages Received", description: "When you receive a new message", icon: Mail },
  { key: "assignment_response", label: "Assignment Graded", description: "When your assignment is graded", icon: GraduationCap },
  { key: "checkin_reminders", label: "Check-In Reminders", description: "When a teacher sends a check-in reminder", icon: ClipboardCheck },
];

export default function Profile({ user: currentUser }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSms, setSavingSms] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    favorite_verse: '',
    picture: '',
    phone_number: ''
  });

  // SMS preferences state
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsPrefs, setSmsPrefs] = useState({
    new_assignments: false,
    journal_response: false,
    forum_posts: false,
    messages_received: false,
    assignment_response: false,
    checkin_reminders: false,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const data = response.data;
      setProfile(data);
      setFormData({
        name: data.name || '',
        bio: data.bio || '',
        favorite_verse: data.favorite_verse || '',
        picture: data.picture || '',
        phone_number: data.phone_number || ''
      });
      setSmsEnabled(data.sms_notifications_enabled || false);
      setSmsPhone(data.phone_number || '');
      setSmsPrefs({
        new_assignments: data.sms_preferences?.new_assignments || false,
        journal_response: data.sms_preferences?.journal_response || false,
        forum_posts: data.sms_preferences?.forum_posts || false,
        messages_received: data.sms_preferences?.messages_received || false,
        assignment_response: data.sms_preferences?.assignment_response || false,
        checkin_reminders: data.sms_preferences?.checkin_reminders || false,
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);

      const response = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = `/api/files/${response.data.file_id}`;
      setFormData(prev => ({ ...prev, picture: fileUrl }));
      toast.success("Image uploaded");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/me`, formData);
      setProfile(response.data);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const saveSmsPreferences = async () => {
    setSavingSms(true);
    try {
      const response = await axios.put(`${API}/users/me/sms-preferences`, {
        sms_notifications_enabled: smsEnabled,
        phone_number: smsPhone || null,
        sms_preferences: smsPrefs
      });
      toast.success("SMS preferences saved");
      // Update local profile data
      setProfile(prev => ({
        ...prev,
        sms_notifications_enabled: response.data.sms_notifications_enabled,
        sms_preferences: response.data.sms_preferences,
        phone_number: response.data.phone_number
      }));
      if (response.data.phone_number) {
        setSmsPhone(response.data.phone_number);
        setFormData(prev => ({ ...prev, phone_number: response.data.phone_number }));
      }
    } catch (error) {
      console.error("Error saving SMS prefs:", error);
      toast.error(error.response?.data?.detail || "Failed to save SMS preferences");
    } finally {
      setSavingSms(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={currentUser}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={currentUser}>
      <div data-testid="profile-page" className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">My Profile</h1>
            <p className="text-slate-600 mt-1">Manage your profile information</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="border-slate-100">
          <CardContent className="pt-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={formData.picture || profile?.picture} />
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-2xl">
                    {getInitials(formData.name || profile?.name)}
                  </AvatarFallback>
                </Avatar>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  size="icon"
                  variant="secondary"
                  className="absolute bottom-0 right-0 rounded-full w-8 h-8"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <div className="mt-4 text-center">
                <Badge variant="outline" className="capitalize">{profile?.role}</Badge>
              </div>
            </div>

            <Separator className="mb-6" />

            {/* Growth Focus - teacher/admin-set, read-only here */}
            {profile?.role === 'student' && profile?.growth_focus && (
              <div data-testid="profile-growth-focus" className="mb-6 p-4 bg-teal-50 rounded-xl border border-teal-200">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-teal-700" />
                  <span className="text-xs font-medium text-teal-700 uppercase tracking-wide">Growth Focus</span>
                </div>
                <p className="text-teal-900">{profile.growth_focus}</p>
                <p className="text-xs text-teal-600 mt-2">Set by your teacher — the one thing to focus on right now.</p>
                {profile.growth_focus_context && (
                  <p className="text-sm text-teal-800/90 mt-3 pt-2 border-t border-teal-200">{profile.growth_focus_context}</p>
                )}
              </div>
            )}

            {/* Profile Form */}
            <div className="space-y-6">
              <div>
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" /> Display Name
                </Label>
                <Input
                  id="name"
                  data-testid="profile-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your display name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" /> Email
                </Label>
                <Input value={profile?.email || ''} disabled className="mt-2 bg-slate-50" />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <Label htmlFor="verse" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-amber-600" /> Favorite Bible Verse
                </Label>
                <Input
                  id="verse"
                  data-testid="profile-verse"
                  value={formData.favorite_verse}
                  onChange={(e) => setFormData({ ...formData, favorite_verse: e.target.value })}
                  placeholder="e.g., John 3:16 - For God so loved the world..."
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  data-testid="profile-bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell us about yourself and your faith journey..."
                  className="mt-2 min-h-[120px]"
                />
              </div>

              {/* Member Since & Last Login */}
              <div className="space-y-2">
                {profile?.created_at && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="w-4 h-4" />
                    Member since {format(new Date(profile.created_at), 'MMMM d, yyyy')}
                  </div>
                )}
                {profile?.last_login && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="w-4 h-4" />
                    Last login: {format(new Date(profile.last_login), "MMMM d, yyyy 'at' h:mm a")}
                  </div>
                )}
              </div>

              <Button
                data-testid="save-profile-btn"
                onClick={saveProfile}
                disabled={saving}
                className="w-full bg-slate-800 hover:bg-slate-900 rounded-full"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SMS Notifications Card */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-amber-600" />
              SMS Notifications
            </CardTitle>
            <CardDescription>
              Receive text message alerts for important activity
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Phone Number */}
            <div>
              <Label htmlFor="sms-phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500" /> Mobile Phone Number
              </Label>
              <Input
                id="sms-phone"
                data-testid="sms-phone"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
                placeholder="+1 (417) 555-0200"
                className="mt-2 max-w-xs"
              />
              <p className="text-xs text-slate-500 mt-1">US numbers only. Format: +14175550200</p>
            </div>

            <Separator />

            {/* Master Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Enable SMS Notifications</p>
                <p className="text-sm text-slate-500">Master toggle for all SMS alerts</p>
              </div>
              <Switch
                data-testid="sms-master-toggle"
                checked={smsEnabled}
                onCheckedChange={setSmsEnabled}
              />
            </div>

            {/* Individual Toggles */}
            {smsEnabled && (
              <div className="space-y-4 pl-1">
                <Separator />
                <p className="text-sm font-medium text-slate-700">Notification Types</p>
                {SMS_PREF_OPTIONS.map((opt) => (
                  <div key={opt.key} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-3">
                      <opt.icon className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{opt.label}</p>
                        <p className="text-xs text-slate-500">{opt.description}</p>
                      </div>
                    </div>
                    <Switch
                      data-testid={`sms-pref-${opt.key}`}
                      checked={smsPrefs[opt.key]}
                      onCheckedChange={(checked) =>
                        setSmsPrefs(prev => ({ ...prev, [opt.key]: checked }))
                      }
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Save SMS Preferences */}
            <Button
              data-testid="save-sms-btn"
              onClick={saveSmsPreferences}
              disabled={savingSms}
              className="w-full bg-amber-600 hover:bg-amber-700 rounded-full"
            >
              {savingSms ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save SMS Preferences
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
